import { useEffect, useMemo } from 'react'

import { theme } from '@hhgtech/hhg-components'
import { LOCALE_HAS_COMMUNITY_SITE } from '@hhgtech/hhg-components/constants'
import { Text } from '@hhgtech/hhg-components/mantine'
import { ChatAi, ChatBubble } from '@hhgtech/icons/other'
import { Box } from '@mantine/core'
import dynamic from 'next/dynamic'
import { shallow } from 'zustand/shallow'

import { getHelloSitesUrl } from 'api/utils/paths'
import AirPopulation from 'components/molecules/AirPopulation'
import useLatestTotalComment from 'components/pages/article/components/useLatestTotalComment'
import { useNavStore } from 'components/templates/navigatorLayout/store'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { CHAT_AI_PATH, HAS_CHAT_AI_SITE } from 'interfaces/constants'
import { ALLOW_AIR_POPUPLATION } from 'interfaces/constants/configs'
import { ArticleReactionType } from 'interfaces/types'
import { useArticleStore } from 'state/article'

import { StyledArticleActionButtonGroup } from './index.styled'
import { SharePopupNew } from '../sharePopupNew'

const ArticleAudioPlayer = dynamic(() =>
  import('components/molecules/articleAudioPlayer').then(
    (mod) => mod.ArticleAudioPlayer,
  ),
)

type ArticleActionButtonGroupProps = {
  articleUrl: string
  isBookmarked?: boolean
  onAskDoctorClick?: () => void
  onBookmarkClick?: () => void
  onLikeClick?: (type: ArticleReactionType) => unknown
  turnOffComment?: boolean
}

const ArticleActionButtonGroup = ({
  articleUrl,
  onAskDoctorClick,
  turnOffComment = false,
}: ArticleActionButtonGroupProps) => {
  const { locale } = useRouterExtend()
  const currentUrl = useCurrentUrl()

  useLatestTotalComment()
  const { totalComment, currentArticle } = useArticleStore(
    (state) => ({
      currentArticle: state.currentArticle,
      totalComment: state.totalComment,
      cycleFontSize: state.cycleFontSize,
    }),
    shallow,
  )
  const isNavSponsor = useNavStore((state) => state.isNavSponsor, shallow)
  const currentDomain = useMemo(() => getHelloSitesUrl(locale), [locale])

  const isCommentEnabled = useMemo(() => {
    return LOCALE_HAS_COMMUNITY_SITE.includes(locale)
  }, [locale])

  useEffect(() => {
    const footer = document.querySelector('footer')
    if (!footer) return
    footer.classList.add('js-with-article-act-btn-group')
    return () => {
      footer.classList.remove('js-with-article-act-btn-group')
    }
  }, [])

  return (
    <StyledArticleActionButtonGroup
      className="action-bar"
      data-is-nav-sponsor={isNavSponsor}
    >
      {ALLOW_AIR_POPUPLATION.includes(locale) && (
        <Box sx={{ paddingBottom: 8 }}>
          <AirPopulation size="md">
            <AirPopulation.IconChart
              dataTracking={{
                dataEventCategory: 'AQI',
                dataEventAction: 'AQI icon click',
                dataEventLabel: 'AQI open pop-up',
              }}
            />
          </AirPopulation>
        </Box>
      )}
      {isCommentEnabled && !turnOffComment && (
        <Box
          className="w-10 h-10 p-2 relative cursor-pointer rounded-lg hover:bg-gray100"
          data-testid="art-question"
          data-event-category="Comments"
          data-event-action="Icon Click"
          data-event-label={currentUrl}
          onClick={onAskDoctorClick}
        >
          <ChatBubble />
          {Number(totalComment) > 0 && (
            <Text
              size="p4"
              className="absolute top-2 left-8"
              color={theme.colors.gray600}
            >
              {totalComment}
            </Text>
          )}
        </Box>
      )}
      <ArticleAudioPlayer
        articleId={currentArticle?.id}
        articleContent={currentArticle?.postContent}
        articleEnableAudio={currentArticle?.enableAudio}
        articleTurnOffArticleVoice={currentArticle?.turnOffArticleVoice}
        isPremium={currentArticle?.isPremium}
        isSidebar
      />
      {HAS_CHAT_AI_SITE.includes(locale) && (
        <Box
          component="a"
          href={currentDomain + CHAT_AI_PATH}
          data-event-category="Chatbot AI"
          data-event-action="Open"
          data-event-label="Article action"
          className="w-10 h-10 py-2 relative cursor-pointer flex justify-center items-end rounded-lg hover:bg-gray100 pointer-event-child-none"
        >
          <ChatAi size={24} color="#2F8AF2" />
          <Text
            size="p5"
            className="!text-[10px]"
            weight="halfBold"
            as="span"
            color="#49A8EA"
            translate="no"
          >
            AI
          </Text>
        </Box>
      )}
      <Box className="h-[1px] w-6 bg-gray200 my-4 mx-auto share-popup-line" />
      <SharePopupNew.AsideNav shareUrl={articleUrl} isRevamp iconSize={24} />
    </StyledArticleActionButtonGroup>
  )
}

export default ArticleActionButtonGroup
